import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  MapPin,
  Users,
  Briefcase,
  Globe,
  Star,
  Calendar,
  Clock,
  Building2,
  CheckCircle,
  Award,
  TrendingUp,
  Heart,
  Share2,
  ArrowLeft,
  Loader2
} from 'lucide-react';
import { apiClient } from '../lib/api-client';
import { useApp } from '@/pages/providers/AppProvider';
import { getAvatarUrl, normalizeMediaUrl } from '@/utils/media';
import { ImageWithFallback } from './ui/ImageWithFallback';
import { JobCardUpdated as JobCard } from './JobCardUpdated';
import { normalizeTier } from '@/utils/tier';
import { SEO } from './SEO';
import { LoadingSpinner } from './LoadingSpinner';
import DOMPurify from 'dompurify';
import { stripHtml, decodeHtml } from '@/utils/text';

interface CompanyDetailPageProps {
  companyName?: string;
  jobs?: any[];
  onViewJob?: (jobId: string) => void;
  onSaveJob?: (jobId: string) => void;
  savedJobs?: string[];
  onNavigate?: (page: string) => void;
}

export function CompanyDetailPage({
  companyName: propCompanyName,
  jobs: propJobs = [],
  onViewJob = () => { },
  onSaveJob = () => { },
  savedJobs = [],
  onNavigate = () => { }
}: CompanyDetailPageProps) {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState('jobs');
  const [companyData, setCompanyData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [jobs, setJobs] = useState<any[]>(propJobs);
  const { companies: globalCompanies, jobs: globalJobs, currentUser, applications } = useApp();

  useEffect(() => {
    if ((!propJobs || propJobs.length === 0) && Array.isArray(globalJobs) && globalJobs.length > 0) {
      setJobs(globalJobs);
    }
  }, [globalJobs, propJobs]);

  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        setLoading(true);
        setError(null);

        let company = null;
        const searchName = propCompanyName || (id ? decodeURIComponent(id) : '');
        const normalizedSearchName = (searchName || '').replace(/[-_]+/g, ' ').trim();

        // Prefer global state first if available
        if (Array.isArray(globalCompanies) && globalCompanies.length > 0) {
          company = globalCompanies.find((c: any) => {
            const nm = (c.name || c.companyName || '').toLowerCase();
            return nm === searchName.toLowerCase() || c.id === id || c._id === id;
          }) || null;
        }

        // If not found in global state, try API calls
        if (!company && searchName) {
          try {
            // First, try to determine if the parameter is an ID or name
            // If it looks like a MongoDB ObjectId (24 hex chars) or UUID, treat as ID
            const isId = /^[0-9a-fA-F]{24}$/.test(searchName) || /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(searchName);

            if (isId) {
              // Try to fetch by ID
              const response = await apiClient.getCompanyById(searchName);
              company = response.company || response;
            } else {
              // Try to fetch by name using the new API
              const response = await apiClient.getCompanyByName(searchName);
              company = response.company || response;
            }
          } catch (err) {
            console.log('Failed to fetch company:', err);
            // If API fails, try searching through all companies as fallback
            try {
              const companiesResponse = await apiClient.getCompanies();
              const companies = companiesResponse.companies || companiesResponse || [];
              if (Array.isArray(companies)) {
                company = companies.find((c: any) =>
                  c.name?.toLowerCase() === searchName.toLowerCase() ||
                  c.companyName?.toLowerCase() === searchName.toLowerCase() ||
                  c.name?.toLowerCase() === normalizedSearchName.toLowerCase() ||
                  c.companyName?.toLowerCase() === normalizedSearchName.toLowerCase()
                );
              }
            } catch (fallbackErr) {
              console.error('Fallback company search failed:', fallbackErr);
            }

            // Final fallback: derive a virtual company from jobs list using normalized name
            if (!company && normalizedSearchName && Array.isArray(jobs) && jobs.length > 0) {
              const fromJobs = jobs.find((j: any) => {
                const nm = (typeof j.company === 'string' ? j.company : j.company?.name) || '';
                return nm.toLowerCase() === normalizedSearchName.toLowerCase();
              });
              if (fromJobs) {
                const logoCandidate = (fromJobs as any).logo || (fromJobs as any).companyLogo || '';
                company = {
                  _id: normalizedSearchName,
                  name: typeof fromJobs.company === 'string' ? fromJobs.company : (fromJobs.company?.name || normalizedSearchName),
                  description: '',
                  industry: (fromJobs as any).category || 'General',
                  size: (fromJobs as any).companySize || 'N/A',
                  location: (fromJobs as any).location || 'Nepal',
                  website: (fromJobs as any).website || '',
                  logo: logoCandidate || getAvatarUrl(normalizedSearchName, 128),
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString()
                } as any;
              }
            }
          }
        }

        if (company) {
          setCompanyData(company);
        } else {
          setError('Company not found');
        }
      } catch (err) {
        console.error('Error fetching company data:', err);
        setError('Failed to load company data');
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyData();
  }, [id, propCompanyName]);

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  if (error || !companyData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Company Not Found</h2>
          <p className="text-gray-600 mb-4">{error || 'The requested company could not be found.'}</p>
          <Button onClick={() => onNavigate('employers')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Companies
          </Button>
        </div>
      </div>
    );
  }

  // Helper function to get company field with fallback
  const getCompanyField = (field: string, fallback: any = '') => {
    return companyData[field] || companyData[field.toLowerCase()] || fallback;
  };

  const getCompanyLogo = (): string => {
    const raw = (companyData as any).logo_url || (companyData as any).logoUrl || (companyData as any).logo || (companyData as any).company_logo || (companyData as any).companyLogo || (companyData as any).profileImage;
    const normalized = raw ? (normalizeMediaUrl(raw) || raw) : '';
    if (normalized) return normalized;
    const nm = (companyData as any).name || (companyData as any).companyName || 'Company';
    return getAvatarUrl(nm, 200);
  };

  // Normalize asset URLs to avoid hardcoded dev ports
  const normalizeAssetUrl = (url: string): string => {
    if (!url) return '';
    const u = String(url);
    // Rewrite localhost uploads (any dev port) to same-origin proxied path
    const m = u.match(/^https?:\/\/(?:localhost|127\.0\.0\.1):\d+(\/uploads\/.*)$/i);
    if (m) {
      const path = m[1];
      const envBase = import.meta.env?.VITE_API_URL as string | undefined;
      let apiOrigin = '';
      try { apiOrigin = envBase ? new URL(envBase).origin : ''; } catch { apiOrigin = envBase || ''; }
      const isBrowser = typeof window !== 'undefined' && !!window.location?.origin;
      const pageOrigin = isBrowser ? window.location.origin : '';
      const pageIsLocal = /localhost|127\.0\.0\.1/i.test(pageOrigin);
      const apiIsLocal = /localhost|127\.0\.0\.1/i.test(apiOrigin);
      if (!pageIsLocal && apiOrigin && !apiIsLocal) {
        return `${apiOrigin}${path}`;
      }
      return path;
    }
    if (u.startsWith('/uploads')) {
      const envBase = import.meta.env?.VITE_API_URL as string | undefined;
      const isAbsoluteEnv = typeof envBase === 'string' && /^https?:\/\//i.test(envBase);
      if (isAbsoluteEnv) {
        try {
          const origin = new URL(envBase!).origin;
          const isBrowser = typeof window !== 'undefined' && !!window.location?.origin;
          const pageOrigin = isBrowser ? window.location.origin : '';
          const pageIsLocal = /localhost|127\.0\.0\.1/i.test(pageOrigin);
          const apiIsLocal = /localhost|127\.0\.0\.1/i.test(origin);
          if (!pageIsLocal && origin && !apiIsLocal) {
            return `${origin}${u}`;
          }
        } catch { }
      }
      return u;
    }
    return u;
  };

  // Extract company information with fallbacks
  const company = {
    name: getCompanyField('name') || getCompanyField('companyName', 'Company Name'),
    logo: getCompanyLogo(),
    industry: getCompanyField('industry', 'Technology'),
    size: getCompanyField('size') || getCompanyField('companySize', '1-50 employees'),
    location: getCompanyField('location') || getCompanyField('address', 'Nepal'),
    founded: getCompanyField('founded') || getCompanyField('establishedYear', 'N/A'),
    website: getCompanyField('website') || getCompanyField('websiteUrl', ''),
    rating: getCompanyField('rating', 4.5),
    description: getCompanyField('description', ''),
    mission: getCompanyField('mission', 'To deliver exceptional services and solutions.'),
    vision: getCompanyField('vision', 'To be a leader in our industry.'),
    specialties: getCompanyField('specialties') || getCompanyField('services', ['Technology Solutions']),
    benefits: getCompanyField('benefits', [
      'Competitive Salary',
      'Health Insurance',
      'Professional Development',
      'Flexible Working Hours'
    ]),
    culture: getCompanyField('culture') || getCompanyField('values', [
      'Innovation',
      'Excellence',
      'Teamwork',
      'Integrity'
    ]),
    offices: (() => {
      const existing = getCompanyField('offices');
      if (Array.isArray(existing) && existing.length > 0) return existing;

      const branches = getCompanyField('branches');
      if (Array.isArray(branches) && branches.length > 0) {
        return branches.map((b: any) => {
          if (typeof b === 'string') {
            return {
              location: 'Branch Office',
              address: b,
              employees: 'N/A'
            };
          }
          return {
            location: b.name || b.location || 'Branch Office',
            address: b.address || b.location || '',
            employees: b.size || b.employees || 'N/A'
          };
        });
      }

      return [{ location: 'Headquarters', address: getCompanyField('location', 'Nepal'), employees: getCompanyField('size', 'N/A') }];
    })()
  };
  // Handle both new and legacy job structure for company filtering
  const companyJobsRaw = Array.isArray(jobs) ? jobs.filter(job => {
    if (!job || !company?.name) return false;

    if (job.company?.name) {
      return job.company.name === company.name;
    }
    // Fallback for legacy structure where company is a string
    return (job as any).company === company.name;
  }) : [];

  const isExpired = (j: any) => {
    const raw = j?.expiryDate || j?.expiry_date || j?.deadline;
    if (!raw) return false;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return false;
    return d.getTime() <= Date.now();
  };

  const companyJobs = [...companyJobsRaw]
    .sort((a, b) => {
      const ea = isExpired(a) ? 1 : 0;
      const eb = isExpired(b) ? 1 : 0;
      if (ea !== eb) return ea - eb; // active first, expired later
      const ad = new Date((a as any).publishedDate || (a as any).postedDate || 0).getTime();
      const bd = new Date((b as any).publishedDate || (b as any).postedDate || 0).getTime();
      return bd - ad;
    });

  const activeCompanyJobsCount = companyJobs.filter(j => !isExpired(j)).length;
  const companyActiveJobs = companyJobs.filter(j => !isExpired(j));

  const handleFollow = () => {
    if (!currentUser) {
      import('sonner').then(({ toast }) => toast.error("Please login to follow the Company"));
      return;
    }
    // TODO: Implement actual follow API call
    import('sonner').then(({ toast }) => toast.success(`You are now following ${company.name}`));
  };

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      import('sonner').then(({ toast }) => toast.success("Link copied to clipboard"));
    }).catch(() => {
      // Fallback
      prompt("Copy this link:", url);
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title={`${company.name} - Careers & Jobs in Nepal`}
        description={stripHtml(`Find latest jobs and careers at ${company.name} in Nepal. ${company.description || ''}`).substring(0, 150) || `View open positions at ${company.name}.`}
        image={getCompanyLogo()}
        type="profile"
      />
      {/* Header Section */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-start justify-between mb-6">
            <Button
              variant="ghost"
              onClick={() => onNavigate('employers')}
              className="mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Companies
            </Button>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" onClick={handleFollow}>
                <Heart className="w-4 h-4 mr-2" />
                Follow
              </Button>
              <Button variant="outline" size="sm" onClick={handleShare}>
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
            </div>
          </div>

          <div className="flex items-start space-x-6">
            <ImageWithFallback
              src={normalizeMediaUrl((company as any).logo_url || company.logo || (company as any).company_logo || (company as any).profileImage || (company as any).cover_image_url || (company as any).coverImageUrl) || (company as any).logo_url || company.logo || (company as any).company_logo || (company as any).profileImage || (company as any).cover_image_url || (company as any).coverImageUrl}
              alt={company.name}
              className="w-24 h-24 rounded-lg object-cover border border-gray-200 bg-gray-50 mr-6"
              fallbackSrc={getAvatarUrl(company.name, 96)}
            />
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{company.name}</h1>
              <div className="flex items-center space-x-4 mb-4">
                <div className="flex items-center space-x-1">
                  <Star className="w-5 h-5 text-yellow-500" />
                  <span className="font-medium">{company.rating}</span>
                  <span className="text-gray-500">• Company Rating</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Briefcase className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">{activeCompanyJobsCount} Open Positions</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div className="flex items-center space-x-2">
                  <Building2 className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-600">{company.industry}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Users className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-600">{company.size}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-600">{company.location}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Globe className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-600">{company.website}</span>
                </div>
              </div>
              {/* Description removed from header; shown in About tab */}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="jobs">Jobs ({activeCompanyJobsCount})</TabsTrigger>
              <TabsTrigger value="about">About</TabsTrigger>
              <TabsTrigger value="culture">Culture</TabsTrigger>
              <TabsTrigger value="offices">Offices</TabsTrigger>
            </TabsList>

            {/* Jobs Tab */}
            <TabsContent value="jobs" className="mt-6">
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-4">
                {companyActiveJobs.map((job) => {
                  const t = normalizeTier((job as any).tier || (job as any).type) || 'latest';
                  const tierColor = t === 'megajob' ? 'bg-gradient-to-r from-yellow-500 to-amber-500 text-white'
                    : t === 'premium' ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white'
                      : t === 'prime' ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
                        : t === 'newspaper' ? 'bg-gradient-to-r from-orange-500 to-yellow-500 text-white'
                          : 'bg-gray-100 text-gray-700';
                  const tierBgColor = t === 'megajob' ? 'border-2 border-yellow-200 bg-gradient-to-br from-yellow-50 to-white'
                    : t === 'premium' ? 'border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white'
                      : t === 'prime' ? 'border-2 border-green-200 bg-gradient-to-br from-green-50 to-white'
                        : t === 'newspaper' ? 'border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-white'
                          : 'border bg-white';
                  return (
                    <div key={job.id} className="job-card-container">
                      <JobCard
                        job={job}
                        onViewJob={onViewJob}
                        onViewCompany={(name: string) => onNavigate(`company/${encodeURIComponent(name)}`)}
                        tierColor={tierColor}
                        tierBgColor={tierBgColor}
                        onSaveJob={onSaveJob}
                        onApply={() => onViewJob(job.id)}
                        isSaved={savedJobs.includes(job.id)}
                        isApplied={currentUser?.type === 'jobseeker' && applications.some(a => String(a.jobId) === String(job.id))}
                        isUserLoggedIn={!!currentUser}
                        imageMode="job"
                      />
                    </div>
                  );
                })}
              </div>

              {companyActiveJobs.length === 0 && (
                <div className="text-center py-12">
                  <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-600 mb-2">No Open Positions</h3>
                  <p className="text-gray-500">This company doesn't have any open positions at the moment.</p>
                </div>
              )}
            </TabsContent>

            {/* About Tab */}
            <TabsContent value="about" className="mt-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Company Description */}
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle>About {company.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {company.description ? (
                      <div
                        className="text-gray-700 leading-relaxed rich-content"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(decodeHtml(company.description)) }}
                      />
                    ) : (
                      <p className="text-gray-500">Company description will be available soon.</p>
                    )}
                  </CardContent>
                </Card>

                {/* Employee Benefits (kept as-is) */}
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle>Employee Benefits</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {company.benefits.map((benefit: string, index: number) => (
                        <div key={index} className="flex items-center space-x-2">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span className="text-sm text-gray-600">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Culture Tab */}
            <TabsContent value="culture" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Company Culture & Values</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {company.culture.map((value: string, index: number) => (
                      <div key={index} className="text-center p-4 bg-blue-50 rounded-lg">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                          <Star className="w-6 h-6 text-blue-600" />
                        </div>
                        <h4 className="font-medium text-gray-900">{value}</h4>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Offices Tab */}
            <TabsContent value="offices" className="mt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {company.offices.map((office: any, index: number) => (
                  <Card key={index}>
                    <CardContent className="p-6">
                      <h3 className="font-bold text-gray-900 mb-2">{office.location}</h3>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <MapPin className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-600">{office.address}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Users className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-600">{office.employees} employees</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
}
